// Helper functions for handling property images
import { API_BASE_URL } from '../config/api';

/** Exported for components that need a reliable `onError` fallback src */
export const FALLBACK_PROPERTY_IMAGE =
  'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80';

export const EXTERNAL_API_SOURCES = new Set([
  'serpapi',
  'hotelsapi',
  'geoapify',
  'google',
  'google_places',
  'street_view',
  'makcorps',
  'external',
  'api',
]);

const needsNoReferrer = (source, imagePath) => {
  const src = String(source || '').toLowerCase();
  const path = String(imagePath || '').toLowerCase();
  if (EXTERNAL_API_SOURCES.has(src)) return true;
  return (
    path.includes('googleusercontent.com') ||
    path.includes('googleapis.com') ||
    path.includes('gstatic.com') ||
    path.includes('serpapi.com') ||
    path.includes('hotels-api.com')
  );
};

/** SerpAPI / Google CDN images need no-referrer to load in the browser. */
export const getExternalImageProps = (source, imagePath) => {
  if (needsNoReferrer(source, imagePath)) {
    return { referrerPolicy: 'no-referrer', crossOrigin: 'anonymous' };
  }
  return {};
};

/** One gallery entry: path string or legacy { url } / { src } */
const coerceImageEntry = (entry) => {
  if (entry == null) return null;
  if (typeof entry === 'string') {
    const t = entry.trim();
    return t || null;
  }
  if (typeof entry === 'object') {
    if (typeof entry.url === 'string' && entry.url.trim()) return entry.url.trim();
    if (typeof entry.src === 'string' && entry.src.trim()) return entry.src.trim();
  }
  return null;
};

/**
 * Safe `url(...)` for CSS `backgroundImage` (quotes + escapes special characters).
 * @param {string} url
 * @returns {string}
 */
export const cssBackgroundUrl = (url) => `url(${JSON.stringify(url || '')})`;

/**
 * All host-uploaded files live under `/uploads/...` on the API server.
 * Rebuild absolute URLs so a wrong host (localhost, LAN IP, stale SITE_URL) still loads in the browser.
 * @param {string} absoluteUrl
 * @returns {string}
 */
const normalizeAbsoluteUploadUrl = (absoluteUrl) => {
  try {
    const parsed = new URL(absoluteUrl);
    if (!parsed.pathname.startsWith('/uploads')) return absoluteUrl;
    const base = API_BASE_URL.replace(/\/$/, '');
    return `${base}${parsed.pathname}${parsed.search}`;
  } catch {
    return absoluteUrl;
  }
};

/**
 * Convert relative image path to full URL
 * @param {string} imagePath - The image path (relative or absolute)
 * @returns {string} - Full URL to the image
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath || typeof imagePath !== 'string') {
    return FALLBACK_PROPERTY_IMAGE;
  }

  const trimmed = imagePath.trim();
  if (!trimmed) {
    return FALLBACK_PROPERTY_IMAGE;
  }

  // API-relative paths (e.g. image proxy)
  if (trimmed.startsWith('/api/')) {
    const base = API_BASE_URL.replace(/\/$/, '');
    return `${base}${trimmed}`;
  }

  // Full URL: normalize /uploads paths to current API base (fixes wrong host in JSON)
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return normalizeAbsoluteUploadUrl(trimmed);
  }

  // Uploaded files: /uploads/... or uploads/... (often wrongly stored without leading slash)
  const uploadPath = trimmed.startsWith('/')
    ? trimmed
    : `/${trimmed.replace(/^\/+/, '')}`;
  if (uploadPath.startsWith('/uploads')) {
    const base = API_BASE_URL.replace(/\/$/, '');
    return `${base}${uploadPath}`;
  }

  // Otherwise, return as-is (might be a data URL or other format)
  return trimmed;
};

/**
 * Get profile picture URL - returns null for Google URLs to avoid 429 from lh3.googleusercontent.com.
 * Use initials/placeholder when null. Migration will replace Google URL with local path.
 */
export const getProfileImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (typeof imagePath === 'string' && imagePath.includes('googleusercontent.com')) return null;
  return getImageUrl(imagePath);
};

/**
 * Get the first image from a property object
 * @param {object} property - The property object
 * @param {string} fallbackUrl - Fallback image URL if no image found
 * @returns {string} - Image URL
 */
export const getPropertyImage = (property, fallbackUrl = FALLBACK_PROPERTY_IMAGE) => {
  if (!property) return fallbackUrl;

  if (Array.isArray(property.images) && property.images.length > 0) {
    const first = property.images.map(coerceImageEntry).find(Boolean);
    if (first) return getImageUrl(first);
  }

  if (property.image && String(property.image).trim()) {
    return getImageUrl(property.image);
  }

  return fallbackUrl;
};

/**
 * Get all images from a property object
 * @param {object} property - The property object
 * @param {array} fallbackImages - Fallback images if no images found
 * @returns {array} - Array of image URLs
 */
export const getPropertyImages = (property, fallbackImages = [FALLBACK_PROPERTY_IMAGE]) => {
  if (!property) return fallbackImages;

  if (Array.isArray(property.images) && property.images.length > 0) {
    return property.images
      .map(coerceImageEntry)
      .filter(Boolean)
      .map((img) => getImageUrl(img));
  }

  // Try to get from image property
  if (property.image) {
    return [getImageUrl(property.image)];
  }

  // Return fallback
  return fallbackImages;
};

export default {
  getImageUrl,
  getProfileImageUrl,
  getPropertyImage,
  getPropertyImages,
  getExternalImageProps,
  cssBackgroundUrl,
  FALLBACK_PROPERTY_IMAGE,
  EXTERNAL_API_SOURCES,
};
